import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Table, Tabs, Button, Form, Input, Upload, DatePicker, Dropdown, Progress, message } from 'antd';
import { uploadFileApi } from '@/services/Selection/case';
import { stageTasksApi, stageTasksSaveApi, stageTasksEditApi, stageTasksSortApi, stageTasksDeleteApi, stageJudgesApi, stageCasesApi, sendMessageApi } from '@/services/Selection/stageMask';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { arrayMoveImmutable } from 'array-move';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { playerStageTaskTypes, judgeStageTaskTypes } from '@utils/constants';
import TinymceEditor from '../../../Editor/TinyMCEEditor';
import './index.less';

// 赛段任务
const StageTaskModal = ({ openStageTaskModal, setOpenStageTaskModal, stageId, title }) => {
    const params = useParams();
    const projectId = ~~params?.pid || 0;

    const [form] = Form.useForm();
    const [judgeData, setJudgesData] = useState([]);
    const [playerData, setPlayerData] = useState([]);
    const [openAddStageModal, setOpenAddStageModal] = useState(false);
    const [activeKey, setActiveKey] = useState('1');
    const [currTaskType, setCurrTaskType] = useState({});

    const [docList, setDocList] = useState(null);
    const [docProgressPercent, setDocProgressPercent] = useState(undefined);
    const [docShowProgress, setDocShowProgress] = useState(false);

    const [loading, setLoading] = useState(false);
    const [judgeMaskItems, setJudgeMaskItems] = useState([]);
    const [playerMaskItems, setPlayerMaskItems] = useState([]);

    const [judgeCount, setJudgeCount] = useState(null); //评委数量
    const [casesCount, stageCasesCount] = useState(null); //实践数量

    const editId = useRef(undefined); // 任务id

    const onChange = key => {
        setActiveKey(key);
    };

    const handleCancel = () => {
        setOpenAddStageModal(false);
        form.resetFields();
    };

    // 保存任务
    const handleOk = () => {
        form.validateFields().then(async values => {
            const reqApi = editId.current ? stageTasksEditApi : stageTasksSaveApi;
            const res = await reqApi({
                id: editId.current,
                name: values.name,
                beginTime: values.beginTime ? dayjs(values.beginTime).format('YYYY-MM-DD HH:mm:ss') : '',
                endTime: values.endTime ? dayjs(values.endTime).format('YYYY-MM-DD HH:mm:ss') : '',
                code: currTaskType?.key,
                form: activeKey,
                businessId: stageId,
                projectId: projectId,
                url: values.url ? (typeof values.url === 'string' ? values.url : JSON.stringify(values.url)) : undefined,
                content: values.content,
            });
            if (res && res.status_code === 200) {
                setOpenAddStageModal(false);
                message.success('保存成功');
                editId.current = undefined;
                form.resetFields();
                getStageTasks(stageId, activeKey);
            }
        });
    };

    const editTask = row => {
        editId.current = row.id;
        setOpenAddStageModal(true);
        form.setFieldsValue({
            name: row.name,
            beginTime: row.beginTime ? dayjs(row.beginTime) : '',
            endTime: row.endTime ? dayjs(row.endTime) : '',
            url: row.url || undefined,
            content: row.content || undefined,
        });
        if (row.code === 'TASK_DOCUMENT' && row.url) {
            form.setFieldValue('url', JSON.parse(row.url));
            setDocList(JSON.parse(row.url));
        }
        const item = (row.form === 1 ? judgeStageTaskTypes : playerStageTaskTypes).find(it => it.key === row.code);
        setCurrTaskType(item);
    };

    const delTask = async delId => {
        const res = await stageTasksDeleteApi({ id: delId });
        if (res.status_code === 200) {
            message.success('删除成功');
            getStageTasks(stageId, activeKey);
        }
    };

    const addTask = item => {
        editId.current = undefined;
        setCurrTaskType(item);
        setOpenAddStageModal(true);
    };

    const sendMsg = () => {
        if (activeKey === '1') {
            if (judgeCount === 0) {
                return Modal.warning({
                    title: '提示',
                    content: '当前赛段您未添加评委名单，请添加后再次发送',
                });
            }
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: `当前赛段已添加“${judgeCount}”位评委，是否发送消息，通知评委查看任务`,
                okText: '确认发送',
                centered: true,
                cancelText: '暂不发送',
                onOk: async () => {
                    const res = await sendMessageApi({ stageId, type: 1 });
                    if (res.status_code === 200) {
                        message.success('发送成功');
                    }
                },
            });
        }
        if (activeKey === '2') {
            if (casesCount === 0) {
                return Modal.warning({
                    title: '提示',
                    content: '当前赛段，您未添加参与评分的实践，请添加实践后再次发送。',
                });
            }
            if (playerData.length === 0) {
                return Modal.warning({
                    title: '提示',
                    content: '当前赛段您未添加任务，请添加任务后再次发送。',
                });
            }
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: `当前赛段已添加“${casesCount}”个实践是否发送消息，通知实践负责人完成赛段任务`,
                okText: '确认发送',
                centered: true,
                cancelText: '暂不发送',
                onOk: async () => {
                    const res = await sendMessageApi({ stageId, type: 2 });
                    if (res.status_code === 200) {
                        message.success('发送成功');
                    }
                },
            });
        }
    };

    const fileProps = {
        name: 'file',
        customRequest: async info => {
            let formatType = null;
            const isJpgOrPng =
                info.file.type === 'application/vnd.ms-powerpoint'
                || info.file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                || info.file.type === 'application/msword'
                || info.file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                || info.file.type === 'application/pdf';
            if (!isJpgOrPng) {
                message.error('只能上传 ppt/doc/pdf 格式');
                return false;
            }
            const isLt30M = info.file.size / 1024 / 1024 < 31;
            if (!isLt30M) {
                message.error('文件大小不能超过30M');
                return;
            }
            if (info.file.type === 'application/vnd.ms-powerpoint') {
                formatType = 'ppt';
            }
            if (info.file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
                formatType = 'pptx';
            }
            if (info.file.type === 'application/msword') {
                formatType = 'doc';
            }
            if (info.file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                formatType = 'docx';
            }
            if (info.file.type === 'application/pdf') {
                formatType = 'pdf';
            }

            const formData = new FormData();
            formData.append('file', info.file);
            formData.append('type', 13);

            setDocShowProgress(true);
            const configParams = {
                onUploadProgress: Progress => {
                    const percents = Math.round((Progress.loaded * 100) / Progress.total);
                    setDocProgressPercent(percents); // 更新进度条
                },
            };

            uploadFileApi(formData, configParams).then(res => {
                if (res && res.status_code === 200 && res.data) {
                    setDocShowProgress(false);
                    setDocProgressPercent(undefined);
                    const docItem = {
                        path: res.data,
                        ext: formatType,
                        name: info.file.name,
                    };
                    setDocList(docItem);

                    form.setFieldValue('url', JSON.stringify(docItem));
                    form.setFields([{ name: 'url', errors: null }]);
                }
            });
        },
        onChange(info) {
            const isJpgOrPng =
                info.file.type === 'application/vnd.ms-powerpoint'
                || info.file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                || info.file.type === 'application/msword'
                || info.file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                || info.file.type === 'application/pdf';
            const isLt30M = info.file.size / 1024 / 1024 < 31;
            if (!isJpgOrPng) {
                info.file.status = 'error';
            }
            if (!isLt30M) {
                info.file.status = 'error';
            }
            if (info.file?.id) {
                info.file.status = 'error';
            }
            if (info.file.status === 'uploading') {
                info.file.status = 'done';
            }
        },
    };

    // 赛段任务列表
    const getStageTasks = async (id, key) => {
        setLoading(true);
        const res = await stageTasksApi({ form: key, stageId: id });
        setLoading(false);
        if (res.status_code === 200 && res.data) {
            if (key === '1') {
                setJudgesData(res.data);
            } else {
                setPlayerData(res.data);
            }
        }
    };

    const dndSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
    );

    const rowListenersRef = useRef({});

    const DragHandle = ({ id }) => <i className="bi bi-list" style={{ cursor: 'grab' }} {...rowListenersRef.current[id]} />;

    const SortableRow = ({ id, ...props }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
        rowListenersRef.current[id] = listeners;
        const style = {
            ...props.style,
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : undefined,
            zIndex: isDragging ? 999 : undefined,
            position: isDragging ? 'relative' : undefined,
        };
        return <tr ref={setNodeRef} style={style} {...props} {...attributes} />;
    };

    const onDragEnd = useCallback(
        async event => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const dataSource = activeKey === '1' ? [...judgeData] : [...playerData];
            const oldIndex = dataSource.findIndex(x => x.id === active.id);
            const newIndex = dataSource.findIndex(x => x.id === over.id);
            if (oldIndex === -1 || newIndex === -1) return;
            const newData = arrayMoveImmutable(dataSource, oldIndex, newIndex);
            if (activeKey === '1') {
                setJudgesData(newData);
            } else {
                setPlayerData(newData);
            }
            const ids = newData.map(item => item.id);
            const res = await stageTasksSortApi([...ids]);
            if (res.status_code === 200) {
                message.success('排序成功');
            }
        },
        [activeKey, judgeData, playerData],
    );

    const currentDataSource = activeKey === '1' ? judgeData : playerData;
    const sortableIds = currentDataSource.map(item => item.id);

    const columns = [
        {
            title: '任务名称',
            dataIndex: 'name',
            className: 'drag-visible',
        },
        {
            title: '开始时间',
            dataIndex: 'beginTime',
        },
        {
            title: '结束时间',
            dataIndex: 'endTime',
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 150,
            render: (_, record) => {
                return (
                    <>
                        <Button size="small" type="link" onClick={() => editTask(record)}>
                            编辑
                        </Button>
                        <Button size="small" type="link" onClick={() => delTask(record.id)}>
                            删除
                        </Button>
                        <DragHandle id={record.id} />
                    </>
                );
            },
        },
    ];

    // 过滤已选中专家任务类型
    const filterJudgeStageTaskTypes = data => {
        const list = judgeStageTaskTypes.filter(item => {
            if (item.key === 'TASK_LINK' || item.key === 'TASK_DOCUMENT' || item.key === 'TASK_TEXT') {
                return item;
            } else {
                return !data.find(judge => judge.code === item.key);
            }
        });
        return list;
    };

    // 过滤已选中实践代表任务类型
    const filterPlayerStageTaskTypes = data => {
        const list = playerStageTaskTypes.filter(item => {
            if (item.key === 'TASK_LINK' || item.key === 'TASK_DOCUMENT' || item.key === 'TASK_TEXT') {
                return item;
            } else {
                return !data.find(judge => judge.code === item.key);
            }
        });
        return list;
    };

    useEffect(() => {
        if (stageId) {
            getStageTasks(stageId, activeKey);
        }
    }, [activeKey, stageId]);

    useEffect(() => {
        document.title = '赛段任务-医评议';
    }, []);

    useEffect(() => {
        const judge = filterJudgeStageTaskTypes(judgeData);
        const player = filterPlayerStageTaskTypes(playerData);
        setJudgeMaskItems(judge);
        setPlayerMaskItems(player);
    }, [judgeData, playerData]);

    useEffect(() => {
        if (stageId) {
            stageJudgesApi({ stageId }).then(res => {
                if (res.status_code === 200) {
                    setJudgeCount(res.data);
                }
            });
            stageCasesApi({ stageId }).then(res => {
                if (res.status_code === 200) {
                    stageCasesCount(res.data);
                }
            });
        }
    }, [stageId]);

    return (
        <>
            <Modal className="stage-mask-modal" title={title} open={openStageTaskModal} centered width={1000} mask={{ closable: false }} onCancel={() => setOpenStageTaskModal(false)} footer={null}>
                <Tabs
                    defaultActiveKey="1"
                    activeKey={activeKey}
                    onChange={onChange}
                    items={[
                        {
                            label: `专家任务（${judgeData.length ? judgeData.length : '未设置'}任务）`,
                            key: '1',
                        },
                        {
                            label: `实践代表任务（${playerData.length ? playerData.length : '未设置'}任务）`,
                            key: '2',
                        },
                    ]}
                />

                <div className="stage-task-tips">
                    <span></span>
                    <div>
                        {/* {(judgeCount !== null || casesCount !== null) &&<Button size="small" type="primary" onClick={sendMsg}>短信通知{activeKey === '1' ? '评委' : '负责人'}</Button>} */}
                        <Dropdown
                            popupRender={() => (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#FFFFFF',
                                        boxShadow: '0 3px 6px -4px #0000001f, 0 6px 16px #00000014, 0 9px 28px 8px #0000000d',
                                    }}
                                >
                                    {(activeKey === '1' ? judgeMaskItems : playerMaskItems).map(item => (
                                        <Button key={item.key} type="link" onClick={() => addTask(item)}>
                                            {item.label}
                                        </Button>
                                    ))}
                                </div>
                            )}
                            placement="bottomRight"
                            arrow
                            destroyOnHidden
                        >
                            <Button size="small" type="primary" style={{ marginLeft: '10px' }}>
                                添加任务
                            </Button>
                        </Dropdown>
                    </div>
                </div>

                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                        <Table
                            pagination={false}
                            dataSource={currentDataSource}
                            columns={columns}
                            rowKey="id"
                            loading={loading}
                            components={{
                                body: {
                                    row: ({ children, ...props }) => <SortableRow id={props['data-row-key']}>{children}</SortableRow>,
                                },
                            }}
                        />
                    </SortableContext>
                </DndContext>
            </Modal>

            <Modal classNames={{ wrapper: 'add-stage-task' }} title="新增任务" open={openAddStageModal} centered width={600} onOk={handleOk} mask={{ closable: false }} onCancel={handleCancel} destroyOnHidden>
                <Form
                    form={form}
                    name="stageTaskForm"
                    labelCol={{
                        span: 4,
                    }}
                    wrapperCol={{
                        span: 18,
                    }}
                    autoComplete="off"
                >
                    <Form.Item label="任务类型：" name="code" className="required">
                        {currTaskType?.label}
                    </Form.Item>
                    <Form.Item
                        label="任务名称："
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: '任务名称不能为空',
                            },
                        ]}
                    >
                        <Input placeholder="请输入任务名称" maxLength={50} />
                    </Form.Item>
                    <Form.Item
                        label="开始时间："
                        name="beginTime"
                        rules={[
                            {
                                required: true,
                                message: '开始时间不能为空',
                            },
                        ]}
                    >
                        <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="结束时间：" name="endTime">
                        <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
                    </Form.Item>

                    {currTaskType?.key === 'TASK_LINK' && (
                        <Form.Item label="跳转链接：" name="url">
                            <Input placeholder="请输入跳转链接" />
                        </Form.Item>
                    )}

                    {currTaskType?.key === 'TASK_DOCUMENT' && (
                        <Form.Item label="上传文档" name="url" extra="支持ppt、doc、pdf格式，大小不超过30MB">
                            <>
                                {docList ? (
                                    <div className="docBox">
                                        {docList.ext === 'pdf' && (
                                            <div className="docIcon" title="PDF 文件">
                                                <i className="bi bi-file-pdf"></i>
                                            </div>
                                        )}
                                        {!!(docList.ext === 'doc' || docList.ext === 'docx') && (
                                            <div className="docIcon" title="Word 文件">
                                                <i className="bi bi-file-word"></i>
                                            </div>
                                        )}
                                        {!!(docList.ext === 'ppt' || docList.ext === 'pptx') && (
                                            <div className="docIcon" title="PowerPoint 文件">
                                                <i className="bi bi-file-ppt"></i>
                                            </div>
                                        )}
                                        <div className="docName">{docList.name}</div>
                                    </div>
                                ) : null}

                                <Upload {...fileProps} maxCount={1} fileList={[]}>
                                    <Button
                                        className="btnWithIcon"
                                        icon={
                                            <>
                                                <i className="bi bi-cloud-arrow-up"></i>
                                            </>
                                        }
                                    >
                                        {docList ? '更换文档' : '上传文档'}
                                    </Button>
                                </Upload>
                                {docShowProgress && <Progress percent={docProgressPercent} />}
                            </>
                        </Form.Item>
                    )}

                    {currTaskType?.key === 'TASK_TEXT' && (
                        <Form.Item label="文本内容：" name="content">
                            <TinymceEditor form={form} fieldValue="content" id="stage-task-content" placeholder="" />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default StageTaskModal;
